<template>
  <div class="page-wrap">

    <!-- Hero banner -->
    <div class="profile-hero">
      <div class="hero-avatar">{{ initials }}</div>
      <div class="hero-info">
        <div class="hero-name">{{ first_name }} {{ last_name }}</div>
        <div class="hero-email">{{ email }}</div>
        <span class="hero-role">{{ currentUser.u.roles }}</span>
      </div>
    </div>

    <div class="profile-grid">

      <!-- Personal Information -->
      <div class="panel-card">
        <div class="panel-header">
          <div class="panel-icon"><i class="fa fa-user"></i></div>
          <div>
            <div class="panel-title">Personal Information</div>
            <div class="panel-sub">Update your name and contact details</div>
          </div>
        </div>

        <div class="panel-body">
          <div class="field-row">
            <div class="field-group">
              <label>First Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="first_name"
                :class="{ 'field-input--error': errors['first_name'] }" placeholder="First name" />
              <span v-if="errors['first_name']" class="field-error">{{ errors['first_name'][0] }}</span>
            </div>
            <div class="field-group">
              <label>Last Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="last_name"
                :class="{ 'field-input--error': errors['last_name'] }" placeholder="Last name" />
              <span v-if="errors['last_name']" class="field-error">{{ errors['last_name'][0] }}</span>
            </div>
          </div>

          <div class="field-group">
            <label>Email Address</label>
            <div class="field-icon-wrap">
              <i class="fa fa-envelope field-icon"></i>
              <input type="email" class="field-input field-input--icon" v-model="email" disabled />
              <span class="verified-badge"><i class="fa fa-check-circle"></i> Verified</span>
            </div>
          </div>

          <template v-if="currentUser.u.roles == 'admin'">
            <div class="field-group">
              <label>Notification Email <span class="req">*</span></label>
              <div class="field-icon-wrap">
                <i class="fa fa-bell field-icon"></i>
                <input type="text" class="field-input field-input--icon" v-model="notification_email"
                  :class="{ 'field-input--error': errors['notification_email'] }"
                  placeholder="Notifications sent here" />
              </div>
              <span v-if="errors['notification_email']" class="field-error">{{ errors['notification_email'][0] }}</span>
            </div>

            <div class="field-row" v-if="!currentUser.u.is_admin">
              <div class="field-group">
                <label>Show Contact</label>
                <div class="field-icon-wrap">
                  <i class="fa fa-phone field-icon"></i>
                  <input type="text" class="field-input field-input--icon" v-model="show_contact"
                    v-mask="'(###)-###-####'" :class="{ 'field-input--error': errors['show_contact'] }"
                    placeholder="(000)-000-0000" />
                </div>
                <span v-if="errors['show_contact']" class="field-error">{{ errors['show_contact'][0] }}</span>
              </div>
              <div class="field-group">
                <label>Display Name</label>
                <input type="text" class="field-input" v-model="display_name"
                  :class="{ 'field-input--error': errors['display_name'] }" placeholder="Public display name" />
                <span v-if="errors['display_name']" class="field-error">{{ errors['display_name'][0] }}</span>
              </div>
            </div>
          </template>
        </div>

        <div class="panel-footer" v-if="!toggle_password">
          <button class="btn-save" @click="update('kt_update_service')" ref="kt_update_service">
            <i class="fa fa-check"></i> Save Changes
          </button>
        </div>
      </div>

      <!-- Security -->
      <div class="panel-card">
        <div class="panel-header">
          <div class="panel-icon orange"><i class="fa fa-lock"></i></div>
          <div>
            <div class="panel-title">Security</div>
            <div class="panel-sub">Manage your password and account access</div>
          </div>
        </div>

        <div class="panel-body">
          <div class="security-hint">
            <i class="fa fa-shield-alt"></i>
            Use a strong, unique password to keep your account secure.
          </div>

          <button class="btn-pw-toggle" :class="{ active: toggle_password }" @click="togglePassword">
            <i class="fa" :class="toggle_password ? 'fa-times' : 'fa-key'"></i>
            {{ toggle_password ? 'Cancel' : 'Change Password' }}
          </button>

          <transition name="slide-down">
            <div v-if="toggle_password" class="pw-fields">
              <div class="field-group">
                <label>Current Password</label>
                <div class="field-icon-wrap">
                  <i class="fa fa-lock field-icon"></i>
                  <input class="field-input field-input--icon field-input--eye" :type="showOld ? 'text' : 'password'"
                    v-model="old_password" :class="{ 'field-input--error': errors.old_password }"
                    placeholder="Current password" />
                  <button class="btn-eye" type="button" @click="showOld = !showOld">
                    <i class="fa" :class="showOld ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                </div>
                <span v-if="errors.old_password" class="field-error">{{ errors.old_password }}</span>
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label>New Password</label>
                  <div class="field-icon-wrap">
                    <i class="fa fa-lock field-icon"></i>
                    <input class="field-input field-input--icon field-input--eye" :type="showNew ? 'text' : 'password'"
                      v-model="new_password" :class="{ 'field-input--error': errors.new_password }"
                      placeholder="New password" />
                    <button class="btn-eye" type="button" @click="showNew = !showNew">
                      <i class="fa" :class="showNew ? 'fa-eye-slash' : 'fa-eye'"></i>
                    </button>
                  </div>
                  <span v-if="errors.new_password" class="field-error">{{ errors.new_password }}</span>
                </div>
                <div class="field-group">
                  <label>Confirm Password</label>
                  <div class="field-icon-wrap">
                    <i class="fa fa-lock field-icon"></i>
                    <input class="field-input field-input--icon field-input--eye"
                      :type="showConfirm ? 'text' : 'password'" v-model="confirm_password"
                      :class="{ 'field-input--error': errors.confirm_password }" placeholder="Confirm password" />
                    <button class="btn-eye" type="button" @click="showConfirm = !showConfirm">
                      <i class="fa" :class="showConfirm ? 'fa-eye-slash' : 'fa-eye'"></i>
                    </button>
                  </div>
                  <span v-if="errors.confirm_password" class="field-error">{{ errors.confirm_password }}</span>
                </div>
              </div>

              <button class="btn-save" @click="updatePassword('kt_update_password')" ref="kt_update_password">
                <i class="fa fa-check"></i> Update Password
              </button>
            </div>
          </transition>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import {
  GET_PROFILE_DATA,
  UPDATE_PROFILE_DATA,
  UPDATE_PROFILE_PASSWORD,
} from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Setting", route: "" },
        { id: 3, title: "Profile", route: "" },
      ],
      errors: [],
      regEmail: /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
      username: "",
      notification_email: "",
      display_name: "",
      first_name: "",
      last_name: "",
      email: "",
      is_active: true,
      toggle_password: false,
      old_password: "",
      new_password: "",
      confirm_password: "",
      show_contact: "",
      showOld: false,
      showNew: false,
      showConfirm: false,
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    initials() {
      const f = this.first_name ? this.first_name.charAt(0).toUpperCase() : "";
      const l = this.last_name ? this.last_name.charAt(0).toUpperCase() : "";
      return f + l || "?";
    },
  },
  created() {
    if (this.$route.name == "admin.profile") {
      this.breadCrumbs[0].route = "admin.dashboard";
    }
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(GET_PROFILE_DATA).then((data) => {
      this.username = data.data.username;
      this.first_name = data.data.first_name;
      this.last_name = data.data.last_name;
      this.email = data.data.email;
      if (this.currentUser.u.roles == "admin") {
        this.notification_email = data.data.notification_email;
        this.show_contact = data.data.show_contact;
        this.display_name = data.data.display_name == null ? "" : data.data.display_name;
      }
    });
  },
  methods: {
    togglePassword() {
      this.toggle_password = !this.toggle_password;
    },
    closeBtnLoad(ref) {
      const btn = this.$refs[ref];
      btn.classList.remove("spinner", "spinner-light", "spinner-right");
      btn.disabled = false;
    },
    loadingButton(ref) {
      const btn = this.$refs[ref];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");
    },
    update(ref) {
      this.errors = [];
      this.loadingButton(ref);
      const _data = {
        username: this.username,
        name: this.first_name + " " + this.last_name,
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        notification_email: this.notification_email.replace(" ", ""),
        display_name: this.display_name,
        status: false,
        show_contact: this.show_contact,
      };
      if (!this.regEmail.test(this.email)) {
        this.closeBtnLoad(ref);
        this.$toastr.e("Please enter a valid email address");
      } else {
        this.$store.dispatch(UPDATE_PROFILE_DATA, { data: _data })
          .then((data) => {
            this.$toastr.s(data.msg);
            this.closeBtnLoad(ref);
            this.$router.push({
              name: this.$route.name == "admin.profile" ? "admin.profile" : "client.profile",
            });
          })
          .catch((err) => {
            this.errors = err.errors;
            this.closeBtnLoad(ref);
          });
      }
    },
    updatePassword(ref) {
      this.errors = [];
      if (!this.toggle_password) return;
      if (!this.new_password) { this.errors.new_password = "This field is required"; return; }
      if (!this.confirm_password) { this.errors.confirm_password = "This field is required"; return; }
      if (this.new_password != this.confirm_password) { this.errors.confirm_password = "Password mismatch"; return; }
      this.loadingButton(ref);
      this.$store.dispatch(UPDATE_PROFILE_PASSWORD, {
        data: { new_password: this.new_password, old_password: this.old_password },
      })
        .then((data) => {
          this.$toastr.s(data.message);
          this.new_password = "";
          this.old_password = "";
          this.confirm_password = "";
          this.toggle_password = false;
          this.closeBtnLoad(ref);
        })
        .catch((err) => {
          this.errors = err;
          this.closeBtnLoad(ref);
          this.new_password = "";
          this.old_password = "";
          this.confirm_password = "";
        });
    },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;
$green: #276749;
$orange: #F58020;

.page-wrap {
  padding: 24px;
  font-family: $font;
}

/* ── Hero ── */
.profile-hero {
  display: flex;
  align-items: center;
  gap: 20px;
  background: linear-gradient(135deg, #1e6fa8 0%, #389ad4 100%);
  border-radius: 16px;
  padding: 28px 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(13, 27, 62, 0.18);
}

.hero-avatar {
  width: 68px;
  height: 68px;
  min-width: 68px;
  border-radius: 14px;
  background: linear-gradient(135deg, $blue, #2176ae);
  border: 3px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 1px;
}

.hero-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hero-name {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

.hero-email {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
}

.hero-role {
  display: inline-block;
  margin-top: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(56, 154, 212, 0.18);
  border: 1px solid rgba(56, 154, 212, 0.35);
  color: #6ec6f0;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

/* ── Grid ── */
.profile-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

/* ── Panel card ── */
.panel-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(13, 27, 62, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #f0f3fa;
  background: #fafbff;
}

.panel-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, $blue, #2176ae);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;

  &.orange {
    background: linear-gradient(135deg, $orange, #d4660a);
  }
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
}

.panel-sub {
  font-size: 11px;
  color: #8a94a6;
  margin-top: 2px;
}

.panel-body {
  padding: 22px 24px;
  flex: 1;
}

.panel-footer {
  padding: 14px 24px;
  border-top: 1px solid #f0f3fa;
  background: #fafbff;
  display: flex;
  justify-content: flex-end;
}

/* ── Form fields ── */
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.field-group {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: #5a6478;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
}

.req {
  color: #e53e3e;
}

.field-icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.field-icon {
  position: absolute;
  left: 12px;
  color: #a0aec0;
  font-size: 12px;
  pointer-events: none;
  z-index: 1;
}

.field-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1.5px solid $border;
  border-radius: 8px;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;

  &:focus {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }

  &:disabled {
    background: #f7f9fc;
    color: #9aa0ae;
    cursor: not-allowed;
  }

  &--icon {
    padding-left: 36px;
  }

  &--eye {
    padding-right: 38px;
  }

  &--error {
    border-color: #e53e3e !important;
    box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.08) !important;
  }
}

.verified-badge {
  position: absolute;
  right: 10px;
  font-size: 11px;
  font-weight: 600;
  color: $green;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 12px;
  }
}

.btn-eye {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: #a0aec0;
  padding: 4px;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;

  &:hover {
    color: $blue;
  }
}

.field-error {
  display: block;
  font-size: 11px;
  color: #e53e3e;
  margin-top: 4px;
}

/* ── Buttons ── */
.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 20px;
  border-radius: 8px;
  border: none;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  i {
    font-size: 12px;
  }

  &:hover {
    background: #2176ae;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-pw-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1.5px solid $border;
  background: #fff;
  color: $dark;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  margin-bottom: 20px;
  transition: all 0.15s;

  i {
    color: $blue;
  }

  &:hover {
    border-color: $blue;
    color: $blue;
    background: #eef7fb;
  }

  &.active {
    border-color: $orange;
    color: $orange;
    background: #fff8f5;

    i {
      color: $orange;
    }
  }
}

/* ── Security hint ── */
.security-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  background: #eef7fb;
  border: 1px solid #c5ddf5;
  border-radius: 8px;
  font-size: 12px;
  color: #1a6fa0;
  margin-bottom: 18px;

  i {
    color: $blue;
    font-size: 14px;
    flex-shrink: 0;
  }
}

.pw-fields {
  overflow: hidden;
}

/* ── Slide transition ── */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: max-height 0.25s ease, opacity 0.25s ease;
  max-height: 500px;
  opacity: 1;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
